import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cl from './Tariff.module.css';
import {
    IconCheck, IconArrowRight, IconStudent, IconTeacher,
} from '../../components/icons';
import B2BService from '../../API/B2BService';
import useReveal from '../../hooks/useReveal';

// Тариф: 3 карточки рядом.
// Раннее бронирование 100к/год (март-июнь) | Стандарт 150к/год (июль-февраль) | 25к/мес
// Один из годовых всегда активный (выделен gradient-рамкой), другой
// dimmed с пояснением «доступно с июля» / «доступно с марта».
//
// Цены и логика месяцев — на бэке в /api/v1/b2b/pricing. Фронт только
// рендерит. Это позволит менять цены без релиза фронта.

const FEATURES_COMMON = [
    '200 учеников с полным доступом',
    '5 преподавателей с логинами',
    'Все 20+ курсов и 700+ уроков',
    '30 000+ задач с автопроверкой',
    'Доступ к 5 Minecraft-серверам',
    '50 проектов на Arduino + 3D',
    'Своя админ-панель центра',
    'Обновления курсов — без доплат',
    'Личная поддержка от автора',
];

const FEATURES_MONTHLY = [
    '200 учеников + 5 преподавателей',
    'Все курсы и серверы',
    'Можно остановить в любой момент',
    'Без обязательств',
];


export default function Tariff() {
    const [pricing, setPricing] = useState(null);

    useEffect(() => {
        B2BService.getPricing().then(setPricing);
    }, []);

    const plans = pricing?.plans || _FALLBACK_PRICING.plans;
    const revealRef = useReveal();

    return (
        <section className={cl.tariff} id="tariff" ref={revealRef}>
            <div className={cl.inner}>
                <div className={cl.head}>
                    <span className={cl.eyebrow} data-reveal="down">Тариф</span>
                    <h2 className={cl.title} data-reveal="up" style={{ '--reveal-delay': '100ms' }}>
                        Один пакет — три платформы
                    </h2>
                    <p className={cl.subtitle} data-reveal="up" style={{ '--reveal-delay': '180ms' }}>
                        Никаких скрытых платежей. Подключение, обновления и
                        обновление контента — уже в стоимости. Оплата по счёту на ИП/ООО.
                    </p>
                </div>

                <div className={cl.grid} data-reveal-stagger>
                    {plans.map((p, i) => (
                        <div key={p.key} data-reveal={i === 1 ? 'pop' : (i % 2 === 0 ? 'left' : 'right')}>
                            <PlanCard plan={p}/>
                        </div>
                    ))}
                </div>

                <div className={cl.foot} data-reveal="up">
                    <b>Все цены без скрытых комиссий.</b> Оплата по счёту на ваше ИП/ООО,
                    закрывающие документы предоставляем.
                </div>

                <div className={cl.extras} data-reveal-stagger>
                    <div className={cl.extra} data-reveal="left">
                        <div className={cl.extraIcon}><IconStudent width={20} height={20}/></div>
                        <div className={cl.extraText}>
                            Доп. ученик сверх 200 — <b>1 000 ₽/мес</b>
                        </div>
                    </div>
                    <div className={cl.extra} data-reveal="right">
                        <div className={cl.extraIcon}><IconTeacher width={20} height={20}/></div>
                        <div className={cl.extraText}>
                            Доп. преподаватель сверх 5 — <b>5 000 ₽/мес</b>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


function PlanCard({ plan }) {
    const isYearly = plan.period === 'год';
    const isActive = plan.is_active;
    const dimmed = isYearly && !isActive; // неактуальный годовой = dim

    let badgeText = plan.badge;
    let badgeCls = cl.badgeActive;
    if (plan.key === 'monthly') {
        badgeCls = cl.badgeMonth;
        if (!badgeText) badgeText = 'Гибко';
    } else if (plan.key === 'yearly_early' && isActive) {
        badgeCls = cl.badgeDiscount;
    }

    const features = plan.key === 'monthly' ? FEATURES_MONTHLY : FEATURES_COMMON;

    const cardClasses = [
        cl.card,
        isActive && plan.key !== 'monthly' && cl.active,
        dimmed && cl.dimmed,
    ].filter(Boolean).join(' ');

    const priceLabel = formatPrice(plan.price);

    return (
        <div className={cardClasses}>
            {badgeText && (
                <span className={`${cl.badge} ${badgeCls}`}>
                    {badgeText}
                </span>
            )}

            <div className={cl.planName}>{plan.name}</div>
            <p className={cl.planDescr}>{plan.description}</p>

            <div className={cl.priceBlock}>
                <span className={cl.priceValue}>{priceLabel} ₽</span>
                <span className={cl.priceUnit}>в {plan.period}</span>
            </div>
            <div className={cl.priceNote}>
                {plan.key === 'yearly_early' && plan.savings_vs_standard
                    && `Экономия ${formatPrice(plan.savings_vs_standard)} ₽`}
                {plan.key === 'yearly_standard' && '—'}
                {plan.key === 'monthly' && 'оплата каждый месяц'}
            </div>

            <ul className={cl.features}>
                {features.map(f => (
                    <li key={f} className={cl.feature}>
                        <span className={cl.check}>
                            <IconCheck width={12} height={12} strokeWidth={3}/>
                        </span>
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            {dimmed ? (
                <button className={`${cl.btn} ${cl.btnDimmed}`} disabled>
                    Доступно с {plan.active_months?.split('-')[0] || 'другого периода'}
                </button>
            ) : (
                <Link to="/registration" className={cl.btn}>
                    {plan.key === 'monthly' ? 'Попробовать' : 'Заказать счёт'}
                    <IconArrowRight width={16} height={16}/>
                </Link>
            )}
        </div>
    );
}


function formatPrice(n) {
    return new Intl.NumberFormat('ru-RU').format(n);
}


// Fallback на случай, если /pricing недоступен (отрендерим с дефолтами)
const _FALLBACK_PRICING = {
    plans: [
        {
            key: 'yearly_early', name: 'Раннее бронирование', price: 100000,
            period: 'год', is_active: false,
            description: 'Скидка для тех, кто решил подключиться к началу учебного года.',
            active_months: 'март-июнь', savings_vs_standard: 50000,
        },
        {
            key: 'yearly_standard', name: 'Стандарт', price: 150000,
            period: 'год', is_active: true,
            description: 'Полный пакет на учебный год.',
            badge: 'Текущий период',
        },
        {
            key: 'monthly', name: 'Помесячно', price: 25000,
            period: 'мес', is_active: true,
            description: 'Гибкая оплата без обязательств. Можно остановить в любой момент.',
        },
    ],
};
